// src/lib/speech/voiceSettings.ts
export type VoiceProvider = "system" | "custom";

export type SpeakMode = "answers" | "help" | "all";

type VoiceSettingsSnapshot = {
  enabled: boolean;
  provider: VoiceProvider;
  speakMode: SpeakMode;
  speakAnswers: boolean;
  volume: number;
};

const LS_VOICE_ENABLED = "bb-voice-enabled";
const LS_VOICE_PROVIDER = "bb-voice-provider"; // "system" | "custom"
const LS_SPEAK_ANSWERS = "bb-voice-speak-answers"; // "true" | "false" (string)
const LS_SPEAK_MODE = "bb-voice-speak-mode"; // "answers" | "help" | "all"
const LS_VOICE_VOLUME = "bb-voice-volume"; // "0".."1" decimal string

const VOICE_SETTINGS_EVENT = "bb-voice-settings-changed";

/**
 * Default playback volume (0..1). 70% strikes a balance per dev-sync note
 * f7e2c436 — the lead-finalize coach voice was too loud when a customer was
 * sitting next to the screen.
 */
export const DEFAULT_VOICE_VOLUME = 0.7;

function emitVoiceSettingsChanged(): void {
  try {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(VOICE_SETTINGS_EVENT));
  } catch {
    // ignore
  }
}

const DEFAULTS = {
  enabled: true,
  provider: "system" as VoiceProvider,
  speakMode: "all" as SpeakMode,
};

function loadSpeakMode(): SpeakMode {
  try {
    const fromMode = localStorage.getItem(LS_SPEAK_MODE);
    if (fromMode === "answers" || fromMode === "help" || fromMode === "all") {
      return fromMode;
    }

    // Backward compat: migrate once from legacy boolean.
    const legacy = localStorage.getItem(LS_SPEAK_ANSWERS);
    const derived: SpeakMode =
      legacy === null
        ? DEFAULTS.speakMode
        : legacy === "true"
        ? "answers"
        : "help";

    // Write forward so the migration is one-time.
    localStorage.setItem(LS_SPEAK_MODE, derived);
    return derived;
  } catch {
    return DEFAULTS.speakMode;
  }
}

export function getVoiceEnabled(): boolean {
  try {
    const raw = localStorage.getItem(LS_VOICE_ENABLED);
    return raw === null ? DEFAULTS.enabled : raw === "true";
  } catch {
    return DEFAULTS.enabled;
  }
}

export function setVoiceEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(LS_VOICE_ENABLED, String(Boolean(enabled)));
    emitVoiceSettingsChanged();
  } catch {
    // ignore
  }
}

export function getVoiceProvider(): VoiceProvider {
  try {
    const raw = localStorage.getItem(LS_VOICE_PROVIDER);
    return raw === "custom" ? "custom" : DEFAULTS.provider;
  } catch {
    return DEFAULTS.provider;
  }
}

export function setVoiceProvider(provider: VoiceProvider): void {
  try {
    localStorage.setItem(LS_VOICE_PROVIDER, provider);
    emitVoiceSettingsChanged();
  } catch {
    // ignore
  }
}

export function getSpeakAnswers(): boolean {
  const mode = getSpeakMode();
  return mode === "answers" || mode === "all";
}

export function setSpeakAnswers(enabled: boolean): void {
  // Legacy API: map to speakMode.
  setSpeakMode(enabled ? "answers" : "help");
}

export function getSpeakMode(): SpeakMode {
  return loadSpeakMode();
}

export function setSpeakMode(mode: SpeakMode): void {
  try {
    localStorage.setItem(LS_SPEAK_MODE, mode);

    // Backward compat mirror (so older code stays in sync).
    localStorage.setItem(
      LS_SPEAK_ANSWERS,
      mode === "answers" || mode === "all" ? "true" : "false"
    );

    emitVoiceSettingsChanged();
  } catch {
    // ignore
  }
}

/**
 * Clamp + parse helper so external callers can't write nonsense to storage.
 * Returns a value in [0, 1]. NaN / out-of-range falls back to the default.
 */
function clampVolume(raw: unknown): number {
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return DEFAULT_VOICE_VOLUME;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

export function getVoiceVolume(): number {
  try {
    const raw = localStorage.getItem(LS_VOICE_VOLUME);
    if (raw === null) return DEFAULT_VOICE_VOLUME;
    return clampVolume(raw);
  } catch {
    return DEFAULT_VOICE_VOLUME;
  }
}

export function setVoiceVolume(volume: number): void {
  try {
    const next = clampVolume(volume);
    localStorage.setItem(LS_VOICE_VOLUME, String(next));
    emitVoiceSettingsChanged();
  } catch {
    // ignore
  }
}

export function getVoiceSettingsSnapshot(): VoiceSettingsSnapshot {
  const speakMode = getSpeakMode();
  return {
    enabled: getVoiceEnabled(),
    provider: getVoiceProvider(),
    speakMode,
    speakAnswers: speakMode === "answers" || speakMode === "all",
    volume: getVoiceVolume(),
  };
}

export function subscribeVoiceSettings(
  onChange: (snapshot: VoiceSettingsSnapshot) => void
): () => void {
  if (typeof window === "undefined") return () => {};

  const notify = () => onChange(getVoiceSettingsSnapshot());

  const onCustom = () => notify();
  const onStorage = (e: StorageEvent) => {
    if (!e.key) return;
    if (
      e.key !== LS_VOICE_ENABLED &&
      e.key !== LS_VOICE_PROVIDER &&
      e.key !== LS_SPEAK_ANSWERS &&
      e.key !== LS_SPEAK_MODE &&
      e.key !== LS_VOICE_VOLUME
    ) {
      return;
    }
    notify();
  };

  window.addEventListener(VOICE_SETTINGS_EVENT, onCustom as EventListener);
  window.addEventListener("storage", onStorage);

  return () => {
    window.removeEventListener(VOICE_SETTINGS_EVENT, onCustom as EventListener);
    window.removeEventListener("storage", onStorage);
  };
}
